
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UserIcon, Mail, KeyIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import { validateEmail, validatePassword, sanitizeString, sanitizeInputValue } from '@/utils/security/inputValidation';
import { getAuthErrorMessage } from '@/utils/auth/authErrorMessages';

interface RegisterFormData {
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
}

const RegisterForm = () => {
  const { t } = useTranslation();
  const { register, isLoading } = useAuth();
  const navigate = useNavigate();
  
  const [registerForm, setRegisterForm] = useState<RegisterFormData>({ 
    name: '', 
    email: '', 
    password: '', 
    confirmPassword: '' 
  });
  
  const [formError, setFormError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleRegisterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    // Sanitize input
    const sanitizedValue = sanitizeInputValue(value, name === 'email' ? 254 : 128);
    setRegisterForm(prev => ({ ...prev, [name]: sanitizedValue }));
    setFormError(''); // Clear error when user types
  };

  const validateForm = () => {
    // Validate email
    const emailValidation = validateEmail(sanitizeString(registerForm.email, 254));
    if (!emailValidation.isValid) {
      throw new Error(emailValidation.error || t('auth.register.errors.invalidEmail'));
    }

    // Validate password
    const passwordValidation = validatePassword(registerForm.password);
    if (!passwordValidation.isValid) {
      throw new Error(passwordValidation.error || t('auth.register.errors.invalidPassword'));
    }
    
    if (registerForm.password !== registerForm.confirmPassword) {
      throw new Error(t('auth.register.errors.passwordMismatch'));
    }
  };

  const handleRegistrationSuccess = () => {
    toast({
      title: t('auth.register.success.title'),
      description: t('auth.register.success.description')
    });
    
    // Clear form
    setRegisterForm({ 
      name: '', 
      email: '', 
      password: '', 
      confirmPassword: '' 
    });
    
    // Navigate based on current location
    if (window.location.pathname === '/register') {
      navigate('/login');
    }
  };

  const handleRegistrationError = (error: any) => {
    console.error('Register form error:', error);
    setFormError(getAuthErrorMessage(error, t));
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    setIsSubmitting(true);
    
    try {
      validateForm();
      
      console.log('Starting registration with data:', { 
        name: registerForm.name, 
        email: registerForm.email 
      });
      
      const success = await register(
        sanitizeString(registerForm.name, 100), 
        sanitizeString(registerForm.email, 254), 
        registerForm.password
      );
      
      if (success) {
        handleRegistrationSuccess();
      } else {
        throw new Error(t('auth.errors.generic'));
      }
    } catch (error: any) {
      handleRegistrationError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const buttonText = isSubmitting ? t('auth.register.creatingAccount') : t('auth.register.createAccount');
  const isDisabled = isLoading || isSubmitting;

  const inputClasses = "w-full";
  const labelClasses = "flex items-center gap-2";
  const buttonClasses = "w-full bg-kbeauty-brown hover:bg-kbeauty-brown/90 text-white";

  return (
    <form onSubmit={handleRegister} className="space-y-3 relative">
      {formError && (
        <div className="bg-destructive/15 p-3 rounded-md text-destructive text-sm">
          {formError}
        </div>
      )}
      
      <div className="space-y-1">
        <Label htmlFor="register-name" className={labelClasses}>
          <UserIcon className="h-4 w-4" />
          {t('auth.register.name')}
        </Label>
        <Input 
          id="register-name" 
          name="name" 
          placeholder={t('auth.register.namePlaceholder')}
          value={registerForm.name} 
          onChange={handleRegisterChange} 
          required 
          disabled={isDisabled}
          className={inputClasses}
        />
      </div>
      
      <div className="space-y-1">
        <Label htmlFor="register-email" className={labelClasses}>
          <Mail className="h-4 w-4" />
          {t('auth.register.email')}
        </Label>
        <Input 
          id="register-email" 
          name="email" 
          type="email" 
          placeholder={t('auth.register.emailPlaceholder')}
          value={registerForm.email} 
          onChange={handleRegisterChange} 
          required 
          disabled={isDisabled}
          className={inputClasses}
        />
      </div>
      
      <div className="space-y-1">
        <Label htmlFor="register-password" className={labelClasses}>
          <KeyIcon className="h-4 w-4" />
          {t('auth.register.password')}
        </Label>
        <Input 
          id="register-password" 
          name="password" 
          type="password" 
          placeholder={t('auth.register.passwordPlaceholder')}
          value={registerForm.password} 
          onChange={handleRegisterChange} 
          required 
          disabled={isDisabled}
          className={inputClasses}
          autoComplete="new-password"
        />
        <p className="text-xs text-muted-foreground">
          {t('auth.register.passwordRequirements')}
        </p>
      </div>
      
      <div className="space-y-1">
        <Label htmlFor="register-confirm-password" className={labelClasses}>
          <KeyIcon className="h-4 w-4" />
          {t('auth.register.confirmPassword')}
        </Label>
        <Input 
          id="register-confirm-password" 
          name="confirmPassword" 
          type="password" 
          placeholder={t('auth.register.passwordPlaceholder')}
          value={registerForm.confirmPassword} 
          onChange={handleRegisterChange} 
          required 
          disabled={isDisabled}
          className={inputClasses}
        />
      </div>
      
      <Button 
        type="submit" 
        className={buttonClasses}
        disabled={isDisabled}
      >
        {buttonText}
      </Button>
    </form>
  );
};

export default RegisterForm;
